import { useRef, useState } from 'react'
import { Check, Globe } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useI18n, type Lang } from '@/i18n'
import { useClickOutside } from '@/hooks/use-click-outside'

/**
 * Language picker. It renders whatever is in the i18n registry, so adding a
 * language to `src/i18n/index.tsx` puts it in this menu automatically — this
 * component is never edited again.
 */
export function LanguageSwitcher({
  className,
  compact = false,
  inline = false,
  placement = 'bottom',
  onDark = false,
}: {
  className?: string
  compact?: boolean
  inline?: boolean
  placement?: 'top' | 'bottom'
  /** Sitting on top of the dark hero — the trigger goes white, the menu does not. */
  onDark?: boolean
}) {
  const { lang, setLang, languages, t } = useI18n()
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)
  useClickOutside(ref, () => setOpen(false), open)

  const current = languages.find((l) => l.code === lang)

  if (inline) {
    return (
      <div
        role="group"
        aria-label={t('lang.label')}
        data-upbo-id="language-switcher"
        data-upbo-label="Language switcher"
        className={cn(
          'inline-flex items-center p-0.5 border rounded-squircle bg-surface-2/60',
          onDark ? 'border-white/25' : 'border-border',
          className,
        )}
      >
        {languages.map((l) => (
          <button
            key={l.code}
            type="button"
            onClick={() => setLang(l.code as Lang)}
            aria-pressed={lang === l.code}
            title={l.label}
            aria-label={l.label}
            className={cn(
              'h-7 px-2.5 grid place-items-center rounded-squircle text-[11px] font-bold uppercase tracking-[0.14em] transition-colors',
              lang === l.code
                ? onDark
                  ? 'bg-white/20 text-white'
                  : 'bg-surface text-text shadow-xs font-black'
                : onDark
                  ? 'text-white/60 hover:text-white'
                  : 'text-text-muted hover:text-text',
            )}
          >
            {l.short}
          </button>
        ))}
      </div>
    )
  }

  return (
    <div ref={ref} className={cn('relative', className)} data-upbo-id="language-switcher" data-upbo-label="Language switcher">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={t('lang.label')}
        className={cn(
          'inline-flex items-center gap-2 h-9 px-2.5 rounded-squircle text-[11px] font-medium uppercase tracking-[0.14em] transition-colors',
          onDark ? 'text-white/80 hover:text-white' : 'text-text-muted hover:text-text',
        )}
      >
        <Globe size={15} strokeWidth={1.5} />
        <span>{current?.short ?? lang.toUpperCase()}</span>
      </button>

      {open && (
        <ul
          role="listbox"
          className={cn(
            'absolute right-0 z-50 min-w-[190px] py-1.5 bg-surface border border-border shadow-float rounded-squircle animate-scale-in',
            placement === 'top' ? 'bottom-full mb-2' : 'top-full mt-2',
            compact && 'right-auto left-0',
          )}
        >
          {languages.map((l) => (
            <li key={l.code}>
              <button
                type="button"
                role="option"
                aria-selected={l.code === lang}
                onClick={() => {
                  setLang(l.code as Lang)
                  setOpen(false)
                }}
                className={cn(
                  'w-full flex items-center justify-between gap-3 px-4 py-2.5 text-sm text-left transition-colors',
                  l.code === lang ? 'text-text' : 'text-text-muted hover:text-text hover:bg-surface-2',
                )}
              >
                <span>{l.label}</span>
                {l.code === lang && <Check size={14} className="text-primary" />}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}
